import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ReadAllResponse } from "@/interfaces";
import { ClientState } from "./clients.entity";
import { listClients } from "./clients.api";
import { Customer, CustomerSortKey } from "@/repositories/client.repository";

const initialState: ClientState = {
  list: [],
  loading: false,
  error: null,
  total: 0,
  filters: {
    page: 1,
    pageSize: 10,
    name: "",
    sortKey: "name",
    sortOrder: "asc",
  },
};

export const fetchClients = createAsyncThunk<
  ReadAllResponse<Customer>,
  void,
  { state: { clients: ClientState } }
>("clients/fetch", async (_, { getState }) => {
  const { page, pageSize, name, sortKey, sortOrder } = getState().clients.filters;
  return await listClients(page, pageSize, name, sortKey, sortOrder);
});

export const fetchClientsWithName = createAsyncThunk<
  ReadAllResponse<Customer>,
  { name: string },
  { state: { clients: ClientState } }
>("clients/fetchWithName", async ({ name }, { getState }) => {
  const { page, pageSize, sortKey, sortOrder } = getState().clients.filters;
  return await listClients(page, pageSize, name, sortKey, sortOrder);
});

const clientSlice = createSlice({
  name: "clients",
  initialState,
  reducers: {
    setPage(state, { payload }: PayloadAction<number>) {
      state.filters.page = payload;
    },
    setPageSize(state, { payload }: PayloadAction<number>) {
      state.filters.pageSize = payload;
    },
    setName(state, { payload }: PayloadAction<string>) {
      state.filters.name = payload;
    },
    setSortKey(state, { payload }: PayloadAction<CustomerSortKey>) {
      if (state.filters.sortKey === payload) {
        state.filters.sortOrder = state.filters.sortOrder === "asc" ? "desc" : "asc";
      } else {
        state.filters.sortKey = payload;
        state.filters.sortOrder = "asc";
      }
    },
  },
  extraReducers: (b) =>
    b
      .addCase(fetchClients.pending, (s) => {
        s.loading = true;
      })
      .addCase(fetchClients.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload.data;
        s.total = a.payload.count ?? 0;
      })
      .addCase(fetchClients.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message ?? "Failed to load clients";
      })
      .addCase(fetchClientsWithName.pending, (s) => {
        s.loading = true;
      })
      .addCase(fetchClientsWithName.fulfilled, (s, a) => {
        s.loading = false;
        s.list = a.payload.data;
        s.total = a.payload.count ?? 0;
      })
      .addCase(fetchClientsWithName.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message ?? "Failed to load clients";
      }),
});

export const { setPage, setPageSize, setName, setSortKey } = clientSlice.actions;
export default clientSlice.reducer;
